import { MantineColor } from '@mantine/core';
import { z } from 'zod';

// Define sub-schema for the last_exam, last_heartworm_lab, and last_bloodwork_lab objects
export const LabSchema = z.object({
    date: z.string(), // ex: "2023-10-10 00:00:00" - always 00:00:00 for now
    products: z.array(z.string()),
});

// Define sub-schema for instructions
export const InstructionsSchema = z.object({
    default: z.string(),
    options: z.array(z.string()),
});
export type Instructions = z.TypeOf<typeof InstructionsSchema>;

export const CompoundReasonsSchema = z.object({
    default: z.string(),
    options: z.array(z.string()),
});

// Sub-schemas for raw parse data
const ParsedValueSchema = z.object({
    value: z.string(),
    failure_reason: z.string().nullable(),
});

export const RawParseDataSchema = z.object({
    product: ParsedValueSchema.nullable(),
    quantity: ParsedValueSchema.nullable(),
    client_name: ParsedValueSchema.nullable(),
    client_phone: ParsedValueSchema.nullable(),
    client_email: ParsedValueSchema.nullable(),
    pet_name: ParsedValueSchema.nullable(),
    pet_species: ParsedValueSchema.nullable(),
    rx_id: ParsedValueSchema.nullable(),
});

export const SubstitutionProductSchema = z.object({
    name: z.string(),
    price_cents: z.number().nullable(),
});
export const SubstitutionRecommendationSchema = z.object({
    recommendation_id: z.string(),
    agent_instructions: z.string(),
    products: z.array(SubstitutionProductSchema),
});

export type SubstitutionRecommendation = z.TypeOf<typeof SubstitutionRecommendationSchema>;

export const GenericSubstitutionMatchSchema = z.object({
    brand_name: z.string(),
    generic_options: z.array(z.string()),
    selected_generic: z.string().nullable(),
    selected_generic_price_cents: z.number().nullable(),
});

export type GenericSubstitutionMatch = z.TypeOf<typeof GenericSubstitutionMatchSchema>;

// All possible statuses
export enum PRESCRIPTION_STATUS {
    AWAITING_CLIENT = 'AWAITING_CLIENT',
    CANCELLED = 'CANCELLED',
    CONVERTED = 'CONVERTED',
    NOT_CONVERTED = 'NOT_CONVERTED',
    PENDING = 'PENDING',
    REJECTED = 'REJECTED',
    TIMED_OUT = 'TIMED_OUT',
    UNKNOWN_PATIENT = 'UNKNOWN_PATIENT',

    // These are fake statuses that we derive on the frontend
    // At some point in the future, we may do some refactoring to support these on the backend fully
    DISCARDED = 'DISCARDED',
    DUPLICATE = 'DUPLICATE',
    PREPARING = 'PREPARING',
    VETSOURCE = 'VETSOURCE',
    VETCOVE = 'VETCOVE',
    SUBSTITUTED = 'SUBSTITUTED',
}

export enum DOCTOR_STATUS {
    APPROVED = 'APPROVED',
    NO_ACTION_NEEDED = 'NO_ACTION_NEEDED',
    PENDING = 'PENDING',
    REJECTED = 'REJECTED',
}

export enum DOCUMENT_SOURCE {
    EMAIL = 'EMAIL',
    FAX = 'FAX',
    PROACTIVE = 'PROACTIVE',
    VETCOVE = 'VETCOVE',
    VETSOURCE = 'VETSOURCE',
    VETSOURCE_3RD_PARTY = 'VETSOURCE_3RD_PARTY',
}

export enum PET_STATUS {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    DECEASED = 'DECEASED',
}

// Recommendation reasons - why a prescription was categorized into its smart inbox section
export enum RECOMMENDATION_REASON {
    // Approval reasons
    ALL_CHECKS_PASSED = 'ALL_CHECKS_PASSED',

    // Rejection reasons
    NO_RECENT_EXAM = 'NO_RECENT_EXAM',
    NO_RECENT_BLOODWORK = 'NO_RECENT_BLOODWORK',
    NO_RECENT_HEARTWORM = 'NO_RECENT_HEARTWORM',
    OUTSIDE_WEIGHT_RANGE = 'OUTSIDE_WEIGHT_RANGE',
    MISMATCHED_SPECIES = 'MISMATCHED_SPECIES',

    // Other reasons
    DECEASED_PATIENT = 'DECEASED_PATIENT',
    INACTIVE_PATIENT = 'INACTIVE_PATIENT',
    REJECTION_OVERRIDEN = 'REJECTION_OVERRIDEN',
    UNKNOWN_PRODUCT = 'UNKNOWN_PRODUCT',

    // Unknown patient reasons
    UNKNOWN_PATIENT = 'UNKNOWN_PATIENT',
}

export const RECOMMENDATION_REASON_LABELS: Record<RECOMMENDATION_REASON, string> = {
    [RECOMMENDATION_REASON.ALL_CHECKS_PASSED]: 'All checks passed',
    [RECOMMENDATION_REASON.NO_RECENT_EXAM]: 'No recent exam',
    [RECOMMENDATION_REASON.NO_RECENT_BLOODWORK]: 'No recent bloodwork',
    [RECOMMENDATION_REASON.NO_RECENT_HEARTWORM]: 'No recent heartworm test',
    [RECOMMENDATION_REASON.OUTSIDE_WEIGHT_RANGE]: 'Outside weight range',
    [RECOMMENDATION_REASON.MISMATCHED_SPECIES]: 'Mismatched species',
    [RECOMMENDATION_REASON.DECEASED_PATIENT]: 'Deceased patient',
    [RECOMMENDATION_REASON.INACTIVE_PATIENT]: 'Inactive patient',
    [RECOMMENDATION_REASON.REJECTION_OVERRIDEN]: 'Rejection overridden',
    [RECOMMENDATION_REASON.UNKNOWN_PRODUCT]: 'Unknown product',
    [RECOMMENDATION_REASON.UNKNOWN_PATIENT]: 'Unknown patient',
};

// Optional fields may be returned from some endpoints but not others
export const PrescriptionSchema = z.object({
    approval_data: z
        .object({
            instructions: z.string(),
            refills: z.string(),
            expiration: z.string().nullable(),
            is_compound: z.boolean(),
            compound_instructions: z.string().nullable(),
            signed_at: z.string(),
            signature_printed_name: z.string().nullable(),
        })
        .nullable()
        .optional(),
    approved_pdf_url: z.string().nullable().optional(),
    compound_reasons: CompoundReasonsSchema.nullable().optional(),
    client_name: z.string().nullable(),
    doctor_status: z.nativeEnum(DOCTOR_STATUS),
    document_source: z.nativeEnum(DOCUMENT_SOURCE).optional(),
    duplicate_of: z.string().nullable().optional(),
    email: z.string().nullable(),
    instructions: InstructionsSchema.nullable().optional(),
    is_compound: z.boolean().optional(),
    last_bloodwork_lab: LabSchema.nullable(),
    last_exam: LabSchema.nullable(),
    last_heartworm_lab: LabSchema.nullable(),
    last_seen_dvm: z.string().nullable().optional(),
    recommendation_reason: z.nativeEnum(RECOMMENDATION_REASON).nullable().optional(),
    note_count: z.number(),
    // TODO: eventually refactor these types since different endpoints return different fields
    // TODO: for example, /inbox returns patient_key, but /list-prescriptions does not
    patient_key: z.string().nullable().optional(),
    pet_age: z.number().nullable().optional(),
    pet_birthdate: z.string().nullable().optional(), // ex: 2019-04-19
    pet_name: z.string().nullable(),
    pet_sex: z.string().nullable().optional(),
    pet_species: z.string().nullable().optional(),
    // Backend returns a number with many decimals potentially - round to 1 decimal place
    pet_weight: z
        .number()
        .transform((val) => (val ? Number(val.toFixed(1)) : null))
        .nullable()
        .optional(),
    pet_weight_unit: z.string().nullable().optional(),
    pet_status: z.nativeEnum(PET_STATUS).nullable().optional(),
    pet_deceased_date: z.string().nullable().optional(), // ex. 2025-02-28
    pet_alerts: z.array(z.string()).nullable().optional(),
    pharmacy: z.string().nullable().optional(),
    pharmacy_rx_id: z.string().nullable(),
    // Deep link into Vetcove's portal for view-only Vetcove orders (DOCUMENT_SOURCE.VETCOVE)
    vetcove_order_portal_url: z.string().nullable().optional(),
    phone_number: z.string().nullable(),
    practice_uuid: z.string(),
    product: z.string().nullable(),
    product_category: z.string().nullable().optional(),
    quantity: z.string().nullable(),
    raw_parse_data: RawParseDataSchema.nullable().optional(),
    reason_to_hide: z.string().nullable().optional(),
    received_at: z.string(), // ex: "2024-07-11 17:19:55.785000+00:00"
    rejected_pdf_url: z.string().nullable().optional(),
    rejection_reasons: z.array(z.string()).nullable().optional(),
    rx_uuid: z.string(),
    status: z.nativeEnum(PRESCRIPTION_STATUS),
    proactive_eligible: z.boolean(),
    substitution_recommendation: SubstitutionRecommendationSchema.nullable().optional(),
    generic_substitution_match: GenericSubstitutionMatchSchema.nullable().optional(),
    vet_substitution_decision: z.enum(['recommended', 'ordered']).nullable().optional(),
    vet_substitution_rejection_reason: z.string().nullable().optional(),
    substitution_accepted_at: z.string().nullable().optional(), // ex: "2025-11-20 04:04:37.529000+00:00"
});

export type Prescription = z.TypeOf<typeof PrescriptionSchema>;

// Expected response for listing prescriptions so we can support pagination
export const PrescriptionsResponseSchema = z.object({
    filtered_total: z.number(),
    results: z.array(PrescriptionSchema),
});

export type PrescriptionsResponse = z.TypeOf<typeof PrescriptionsResponseSchema>;

// Prescriptions come formatted differently for the Smart Inbox
export const PrescriptionsForInboxSchema = z.object({
    prescriptions: z.object({
        approve: z.array(PrescriptionSchema),
        other: z.array(PrescriptionSchema),
        reject: z.array(PrescriptionSchema),
        unknown_patient: z.array(PrescriptionSchema),
    }),
});

export type PrescriptionsForInboxResponse = z.TypeOf<typeof PrescriptionsForInboxSchema>;

// Smart Inbox sections
export enum SMART_INBOX_SECTIONS {
    APPROVE = 'Likely approve',
    REJECT = 'Likely reject',
    OTHER = 'Need further review',
    UNKNOWN_PATIENT = 'Unknown patients',
}

// Various actions that can be taken on a prescription
export enum PRESCRIPTION_ACTION {
    // Doctor decisions
    APPROVE = 'APPROVE',
    REJECT = 'REJECT',
    APPROVE_VETSOURCE = 'APPROVE_VETSOURCE',
    REJECT_VETSOURCE = 'REJECT_VETSOURCE',

    // Send back to external pharmacy immediately
    SENDBACK = 'SENDBACK',

    // Add client to DNC list
    ADD_CLIENT_TO_DNC = 'ADD_CLIENT_TO_DNC',

    // Reassign prescription to another client
    REASSIGN_CLIENT = 'REASSIGN_CLIENT',

    // Reset prescription to pending
    RESET = 'RESET',

    // Override client decision
    UNCONVERT = 'UNCONVERT',
    MARK_AS_CONVERTED_FROM_NOT_CONVERTED = 'MARK_AS_CONVERTED_FROM_NOT_CONVERTED',
    MARK_AS_CONVERTED_FROM_AWAITING_CLIENT = 'MARK_AS_CONVERTED_FROM_AWAITING_CLIENT',

    // Refax document back to third party pharmacy
    REFAX = 'REFAX',

    // Remove duplicate status
    REMOVE_DUPLICATE = 'REMOVE_DUPLICATE',

    // Undiscard prescription
    UNDISCARD = 'UNDISCARD',

    // Remove client from call list
    REMOVE_CLIENT_FROM_CALL_LIST = 'REMOVE_CLIENT_FROM_CALL_LIST',

    // Writeback prescription
    WRITEBACK_PRESCRIPTION = 'WRITEBACK_PRESCRIPTION',
}

// Options for the compound instructions select
export const COMPOUND_REASONS_OPTIONS = [
    'The pet will not take the approved product due to its flavor',
    "The pet's dosage cannot be achieved with the approved product",
    'No approved products are marketed',
    'The pet is unable to safely swallow the approved product due to the size of the dosage form',
    'The pet requires a different route of administration than is available with the approved product',
    'The approved product is temporarily unavailable due to a shortage',
];

// Rejection reasons
export enum REJECTION_REASON {
    EXAM_NEEDED = 'EXAM_NEEDED',
    HEARTWORM_TEST_NEEDED = 'HEARTWORM_TEST_NEEDED',
    BLOODWORK_NEEDED = 'BLOODWORK_NEEDED',
    WRONG_DOSAGE = 'WRONG_DOSAGE',
    MOVED_PRACTICES = 'MOVED_PRACTICES',
    NOT_A_PATIENT = 'NOT_A_PATIENT',
    DUPLICATE = 'DUPLICATE',
    PRESCRIPTION_HANDLED_BY_PRACTICE = 'PRESCRIPTION_HANDLED_BY_PRACTICE',
    CLIENT_ORDERED_ELSEWHERE = 'CLIENT_ORDERED_ELSEWHERE',
    WRITTEN_SCRIPT_PROVIDED_TO_CLIENT = 'WRITTEN_SCRIPT_PROVIDED_TO_CLIENT',
    PET_PASSED_AWAY = 'PET_PASSED_AWAY',
    OTHER = 'OTHER',
}

export const REJECTION_REASONS: { value: REJECTION_REASON; label: string }[] = [
    {
        value: REJECTION_REASON.EXAM_NEEDED,
        label: 'Exam needed',
    },
    {
        value: REJECTION_REASON.HEARTWORM_TEST_NEEDED,
        label: 'Heartworm test needed',
    },
    {
        value: REJECTION_REASON.BLOODWORK_NEEDED,
        label: 'Bloodwork needed',
    },
    {
        value: REJECTION_REASON.WRONG_DOSAGE,
        label: 'Wrong dosage',
    },
    {
        value: REJECTION_REASON.MOVED_PRACTICES,
        label: 'Moved practices',
    },
    {
        value: REJECTION_REASON.NOT_A_PATIENT,
        label: 'Not a patient',
    },
    {
        value: REJECTION_REASON.DUPLICATE,
        label: 'Duplicate',
    },
    {
        value: REJECTION_REASON.PRESCRIPTION_HANDLED_BY_PRACTICE,
        label: 'Prescription handled by practice',
    },
    {
        value: REJECTION_REASON.CLIENT_ORDERED_ELSEWHERE,
        label: 'Client ordered elsewhere',
    },
    {
        value: REJECTION_REASON.WRITTEN_SCRIPT_PROVIDED_TO_CLIENT,
        label: 'Written prescription provided to client',
    },
    {
        value: REJECTION_REASON.PET_PASSED_AWAY,
        label: 'Pet passed away',
    },
    {
        value: REJECTION_REASON.OTHER,
        label: 'Other',
    },
];

export enum COMMON_PHARMACY_NAME {
    CHEWY = 'Chewy',
    PETCO = 'Petco',
    PET_MEDS = 'PetMeds',
    OTHER = 'Other',
}

export const COMMON_PHARMACY_NAMES: { value: COMMON_PHARMACY_NAME; label: string }[] = [
    {
        value: COMMON_PHARMACY_NAME.CHEWY,
        label: 'Chewy',
    },
    {
        value: COMMON_PHARMACY_NAME.PETCO,
        label: 'Petco',
    },
    {
        value: COMMON_PHARMACY_NAME.PET_MEDS,
        label: 'PetMeds',
    },
    {
        value: COMMON_PHARMACY_NAME.OTHER,
        label: 'Other',
    },
];

// Reasons for removing a client from the call list
export enum CALL_LIST_REMOVAL_REASON {
    DUPLICATE_SCRIPT = 'DUPLICATE_SCRIPT',
    CLINIC_REACHING_OUT = 'CLINIC_REACHING_OUT',
    CLINIC_INSTRUCTED_NOT_TO_CALL = 'CLINIC_INSTRUCTED_NOT_TO_CALL',
    PET_PASSED_AWAY = 'PET_PASSED_AWAY',
    CALL_PARAMETERS_MET = 'CALL_PARAMETERS_MET',
    COMPOUNDED_SCRIPT = 'COMPOUNDED_SCRIPT',
    CONTROLLED_DRUG = 'CONTROLLED_DRUG',
    MISMATCHED_CLIENT_PATIENT = 'MISMATCHED_CLIENT_PATIENT',
    OTHER = 'OTHER',
}

export const CALL_LIST_REMOVAL_REASONS: { value: CALL_LIST_REMOVAL_REASON; label: string }[] = [
    {
        value: CALL_LIST_REMOVAL_REASON.DUPLICATE_SCRIPT,
        label: 'Duplicate script',
    },
    {
        value: CALL_LIST_REMOVAL_REASON.CLINIC_REACHING_OUT,
        label: 'Clinic reaching out',
    },
    {
        value: CALL_LIST_REMOVAL_REASON.CLINIC_INSTRUCTED_NOT_TO_CALL,
        label: 'Clinic instructed not to call',
    },
    {
        value: CALL_LIST_REMOVAL_REASON.PET_PASSED_AWAY,
        label: 'Pet passed away',
    },
    {
        value: CALL_LIST_REMOVAL_REASON.CALL_PARAMETERS_MET,
        label: 'Call parameters already met',
    },
    {
        value: CALL_LIST_REMOVAL_REASON.COMPOUNDED_SCRIPT,
        label: 'Compounded script',
    },
    {
        value: CALL_LIST_REMOVAL_REASON.CONTROLLED_DRUG,
        label: 'Controlled drug',
    },
    {
        value: CALL_LIST_REMOVAL_REASON.MISMATCHED_CLIENT_PATIENT,
        label: 'Mismatched client and patient',
    },
    {
        value: CALL_LIST_REMOVAL_REASON.OTHER,
        label: 'Other',
    },
];

const NoteSchema = z.object({
    id: z.string(),
    message: z.string(),
    created_at: z.string(), // ex: "2024-07-17 20:35:58.289000+00:00"
    created_by: z.string(),
});

export const NotesResponseSchema = z.object({
    notes: z.array(NoteSchema),
    rx_info: z.object({
        client_name: z.string().nullable(),
        patient_name: z.string().nullable(),
        product: z.string().nullable(),
    }),
});

export type Note = z.TypeOf<typeof NoteSchema>;
export type NotesResponse = z.TypeOf<typeof NotesResponseSchema>;

export const GeneratePrescriptionResponseSchema = z.object({
    download_url: z.string(),
});

// Match status enum to labels and colors we want to actually display to the user
interface StatusBadgeInfo {
    label: string;
    color: MantineColor;
}

export const PRESCRIPTION_STATUS_MAP: Record<PRESCRIPTION_STATUS, StatusBadgeInfo> = {
    [PRESCRIPTION_STATUS.AWAITING_CLIENT]: {
        label: 'Awaiting client',
        color: 'gray',
    },
    [PRESCRIPTION_STATUS.CANCELLED]: {
        label: 'Cancelled',
        color: 'gray',
    },
    [PRESCRIPTION_STATUS.CONVERTED]: {
        label: 'Converted',
        color: 'green',
    },
    [PRESCRIPTION_STATUS.NOT_CONVERTED]: {
        label: 'Not converted',
        color: 'red',
    },
    [PRESCRIPTION_STATUS.PENDING]: {
        label: 'Pending',
        color: 'yellow',
    },
    [PRESCRIPTION_STATUS.REJECTED]: {
        label: 'Rejected',
        color: 'red',
    },
    [PRESCRIPTION_STATUS.TIMED_OUT]: {
        label: 'Timed out',
        color: 'gray',
    },
    [PRESCRIPTION_STATUS.UNKNOWN_PATIENT]: {
        label: 'Unknown patient',
        color: 'orange',
    },
    [PRESCRIPTION_STATUS.DISCARDED]: {
        label: 'Discarded',
        color: 'gray',
    },
    [PRESCRIPTION_STATUS.DUPLICATE]: {
        label: 'Duplicate',
        color: 'gray',
    },
    [PRESCRIPTION_STATUS.PREPARING]: {
        label: 'Preparing',
        color: 'gray',
    },
    [PRESCRIPTION_STATUS.VETSOURCE]: {
        label: 'Vetsource',
        color: '#78bb43',
    },
    // Vetcove brand blue — keep in sync with the .vc-teal class in globals.css.
    [PRESCRIPTION_STATUS.VETCOVE]: {
        label: 'Vetcove',
        color: '#2397c8',
    },
    [PRESCRIPTION_STATUS.SUBSTITUTED]: {
        label: 'Substituted',
        color: 'blue',
    },
};

export const DOCTOR_STATUS_MAP: Record<DOCTOR_STATUS, StatusBadgeInfo> = {
    [DOCTOR_STATUS.APPROVED]: {
        label: 'Approved',
        color: 'green',
    },
    [DOCTOR_STATUS.NO_ACTION_NEEDED]: {
        label: 'No action needed',
        color: 'gray',
    },
    [DOCTOR_STATUS.PENDING]: {
        label: 'Pending',
        color: 'yellow',
    },
    [DOCTOR_STATUS.REJECTED]: {
        label: 'Rejected',
        color: 'red',
    },
};

export function isVetsource(document_source?: DOCUMENT_SOURCE): boolean {
    if (!document_source) return false;

    return [DOCUMENT_SOURCE.VETSOURCE, DOCUMENT_SOURCE.VETSOURCE_3RD_PARTY].includes(
        document_source,
    );
}

// Vetcove orders are view-only in our portal — vets approve/reject them in
// Vetcove's own portal, so we surface a "View in Vetcove" deep link instead of
// the in-dashboard approve/reject flow used for Vetsource.
export function isVetcove(document_source?: DOCUMENT_SOURCE): boolean {
    return document_source === DOCUMENT_SOURCE.VETCOVE;
}
